import {
  createFocusTrap,
} from 'focus-trap';

import {
  Mode,
} from './ResponsiveNav.constants.js';
import { nextMacroTask } from '../../utilities/queue.js';

/**
 * @typedef {import('focus-trap').FocusTrap} FocusTrap
 */
export class ResponsiveNav extends HTMLElement {
  /** @param {string} tagName */
  static define(tagName = 'responsive-nav') {
    if ('customElements' in window) {
      customElements.define(tagName, ResponsiveNav);
    }
  }

  /** @returns {string[]} */
  static get observedAttributes() {
    return ['mode'];
  }

  /** @returns {?HTMLElement} */
  get targetElement() {
    return this.#menu;
  }

  /** @param {?HTMLElement} value */
  set targetElement(value) {
    this.#menu = value;
  }

  /** @returns {?string} */
  get target() {
    return this.getAttribute('target');
  }

  /** @param {?string} value */
  set target(value) {
    if (value) {
      this.setAttribute('target', value);
    } else {
      this.removeAttribute('target');
    }
  }

  /** @returns {?string} */
  get mode() {
    return this.getAttribute('mode');
  }

  /** @param {?string} value */
  set mode(value) {
    if (value) {
      this.setAttribute('mode', value);
    } else {
      this.removeAttribute('mode');
    }
  }

  /** @returns {?string} */
  get breakpoint() {
    return this.getAttribute('breakpoint');
  }

  /** @param {?string} value */
  set breakpoint(value) {
    if (value) {
      this.setAttribute('breakpoint', value);
    } else {
      this.removeAttribute('breakpoint');
    }
  }

  /** @type {boolean} */
  #isInitialized = false;

  /** @type {?HTMLElement} */
  #menu = null;

  /** @type {?FocusTrap} */
  #focusTrap = null;

  /** @type {?MediaQueryList} */
  #mediaQuery = null;

  constructor() {
    super();

    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.handleMenuToggle = this.handleMenuToggle.bind(this);
  }

  /**
   * @returns {void}
   */
  connectedCallback() {
    if (! this.breakpoint) {
      throw new Error(`<${this.localName}> must provide a breakpoint at which the element should switch between the different modes.`);
    }

    this.#menu = /** @type {?HTMLElement} */ (
      this.target ? document.getElementById(this.target) : this.nextElementSibling
    );

    if (this.#menu === null) {
      throw new Error(`<${this.localName}> must reference a target element`);
    }

    this.#mediaQuery = matchMedia(this.breakpoint);

    this.#mediaQuery.addEventListener('change', this.handleBreakpointChange);
    this.#menu.addEventListener('toggle', this.handleMenuToggle);

    this.#setMode(this.#mediaQuery.matches ? Mode.MenuBar : Mode.Dialog);

    this.#isInitialized = true;
  }

  /**
   * @returns {void}
   */
  disconnectedCallback() {
    this.#focusTrap?.deactivate();

    this.#mediaQuery?.removeEventListener('change', this.handleBreakpointChange);
    this.#menu?.removeEventListener('toggle', this.handleMenuToggle);

    this.#menu = null;

    this.#focusTrap = null;
    this.#mediaQuery = null;

    this.#isInitialized = false;
  }

  /**
   * @param {string} name
   * @param {?string} oldValue
   * @param {?string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (! this.#isInitialized || oldValue === newValue) {
      return;
    }

    if (name === 'mode') {
      this.#setMode(newValue, oldValue);
    }
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  handleBreakpointChange(event) {
    this.mode = event.matches ? Mode.MenuBar : Mode.Dialog;
  }

  /**
   * @param {Event} event
   */
  handleMenuToggle(event) {
    if (this.mode !== Mode.Dialog || this.#menu === null) {
      return;
    }

    const {newState} = /** @type {CustomEvent} */ (event).detail;

    if (newState === 'open') {
      this.#focusTrap ??= createFocusTrap(this.#menu);
      this.#focusTrap?.activate();
    } else {
      this.#focusTrap?.deactivate();
    }
  }

  /**
   * @param {?string} newMode
   * @param {?string} [oldMode]
   * @returns {Promise<void>}
   */
  async #setMode(newMode, oldMode = null) {
    if (oldMode === Mode.Dialog) {
      this.#teardownDialog();
    } else if (oldMode === Mode.MenuBar) {
      this.#teardownMenuBar();
    }

    this.setAttribute('transitioning', '');

    if (newMode) {
      this.setAttribute('mode', newMode);
    } else {
      this.removeAttribute('mode');
    }

    await nextMacroTask();
    this.removeAttribute('transitioning');

    if (newMode === Mode.Dialog) {
      this.#setupDialog();
    } else if (newMode === Mode.MenuBar) {
      this.#setupMenuBar();
    }
  }

  /**
   * @returns {void}
   */
  #setupDialog() {
    this.#menu?.setAttribute('role', 'dialog');
    this.#menu?.setAttribute('aria-modal', 'true');
    this.#menu?.setAttribute('aria-label', 'Menü');
    this.#menu?.setAttribute('tabindex', '-1');

    this.#menu?.querySelector('ol, ul')?.classList.add('scrollable');
    this.#menu?.querySelector('ol, ul')?.setAttribute('data-orientation', 'vertical');
  }

  /**
   * @returns {void}
   */
  #teardownDialog() {
    this.#focusTrap?.deactivate();

    this.#menu?.removeAttribute('role');
    this.#menu?.removeAttribute('aria-modal');
    this.#menu?.removeAttribute('aria-label');
    this.#menu?.removeAttribute('tabindex');

    this.#menu?.querySelector('ol, ul')?.classList.remove('scrollable');
    this.#menu?.querySelector('ol, ul')?.removeAttribute('data-orientation');
  }

  /**
   * @returns {void}
   */
  #setupMenuBar() {
    // Empty
  }

  /**
   * @returns {void}
   */
  #teardownMenuBar() {
    // Empty
  }
}